import React, { useState, useRef, useEffect } from 'react';
import ExportButton from './ExportButton';
import { COLORS } from '../constants';

function UserMenu({ session, tickets, boards, onSignOut, onCSVUpload, isConnected }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const fileInputRef = useRef(null);
  const menuRef = useRef(null);

  const getUserInitials = () => {
    const user = session?.user;
    if (!user) return '';

    if (user.user_metadata?.full_name) {
      const nameParts = user.user_metadata.full_name.split(' ');
      return nameParts.map((n) => n[0]).join('').toUpperCase();
    } else if (user.email) {
      return user.email[0].toUpperCase();
    }
    return '';
  };

  const handleCSVUploadClick = () => {
    fileInputRef.current?.click();
    setIsMenuOpen(false);
  };

  const handleCSVUpload = (e) => {
    onCSVUpload(e);
    setIsMenuOpen(false);
  };

  const handleExportClick = () => {
    // The ExportButton handles its own export logic
    setIsMenuOpen(false);
  };

  const handleSignOut = () => {
    onSignOut();
    setIsMenuOpen(false);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  return (
    <div style={{ position: 'relative' }} ref={menuRef}>
      {/* Avatar Button with Connection Status Badge */}
      <div style={{ position: 'relative' }}>
        <div
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          title={session?.user?.email}
          style={{
            width: '35px',
            height: '35px',
            borderRadius: '50%',
            backgroundColor: COLORS.SECONDARY,
            color: COLORS.WHITE,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: '500',
            fontSize: '16px',
            textTransform: 'uppercase',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            border: '2px solid rgba(255, 255, 255, 0.2)',
            boxShadow: isMenuOpen ? '0 0 0 3px rgba(255, 255, 255, 0.3)' : 'none'
          }}
          onMouseEnter={(e) => {
            if (!isMenuOpen) {
              e.target.style.transform = 'scale(1.05)';
              e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)';
            }
          }}
          onMouseLeave={(e) => {
            if (!isMenuOpen) {
              e.target.style.transform = 'scale(1)';
              e.target.style.boxShadow = 'none';
            }
          }}
        >
          {getUserInitials()}
        </div>

        {/* Connection Status Badge - Bottom Right of Avatar */}
        <div
          style={{
            position: 'absolute',
            bottom: '-2px',
            right: '-2px',
            width: '14px',
            height: '14px',
            borderRadius: '50%',
            backgroundColor: isConnected ? '#28a745' : '#dc3545',
            border: '2px solid white',
            boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
            zIndex: 1
          }}
          title={isConnected ? 'Connected to real-time updates' : 'Disconnected'}
        />
      </div>

      {/* Dropdown Menu */}
      {isMenuOpen && (
        <>
          {/* Backdrop */}
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 999,
              background: 'transparent'
            }}
            onClick={() => setIsMenuOpen(false)}
          />
          
          {/* Menu */}
          <div
            style={{
              position: 'absolute',
              top: '50px',
              right: 0,
              background: 'white',
              borderRadius: '12px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
              border: '1px solid #e0e0e0',
              zIndex: 1000,
              minWidth: '220px',
              overflow: 'hidden',
              animation: 'menuFadeIn 0.2s ease-out'
            }}
          >
            {/* User Info Header */}
            <div style={{
              padding: '16px',
              borderBottom: '1px solid #f0f0f0',
              backgroundColor: '#f8f9fa'
            }}>
              <div style={{ 
                fontWeight: '600', 
                fontSize: '14px', 
                color: '#333',
                marginBottom: '4px'
              }}>
                {session?.user?.user_metadata?.full_name || 'User'}
              </div>
              <div style={{ 
                fontSize: '12px', 
                color: '#666',
                wordBreak: 'break-word'
              }}>
                {session?.user?.email}
              </div>
              {/* Connection status in menu */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                marginTop: '8px',
                fontSize: '11px',
                color: isConnected ? '#28a745' : '#dc3545'
              }}>
                <div style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  backgroundColor: isConnected ? '#28a745' : '#dc3545'
                }} />
                {isConnected ? 'Connected' : 'Disconnected'}
              </div>
            </div>

            {/* Menu Items */}
            <div style={{ padding: '8px 0' }}>
              {/* Import CSV */}
              <button
                onClick={handleCSVUploadClick}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  width: '100%',
                  padding: '12px 16px',
                  background: 'none',
                  border: 'none',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: '#333',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                <span style={{ fontSize: '16px' }}>📤</span>
                <span>Import CSV</span>
              </button>

              {/* Export CSV */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  width: '100%',
                  padding: '12px 16px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: '#333',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                onClick={handleExportClick}
              >
                <span style={{ fontSize: '16px' }}>📥</span>
                <div style={{ flex: 1 }}>
                  <ExportButton 
                    tickets={tickets} 
                    boards={boards}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      color: 'inherit',
                      fontSize: 'inherit',
                      cursor: 'inherit',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  />
                </div>
              </div>

              {/* Divider */}
              <div style={{
                height: '1px',
                backgroundColor: '#f0f0f0',
                margin: '8px 0'
              }} />

              {/* Logout */}
              <button
                onClick={handleSignOut}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  width: '100%',
                  padding: '12px 16px',
                  background: 'none',
                  border: 'none',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: '#dc3545',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#fff5f5'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                <span style={{ fontSize: '16px' }}>🚪</span>
                <span>Logout</span>
              </button>
            </div>
          </div>
        </>
      )}

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        onChange={handleCSVUpload}
        style={{ display: 'none' }}
      />

      <style jsx>{`
        @keyframes menuFadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
      `}</style>
    </div>
  );
}

export default UserMenu;